import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, RefreshCw, ShieldCheck, Inbox, ChevronDown, ChevronUp } from "lucide-react";
import { Eyebrow } from "@/components/site/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "temrp_token";

const inputCls = "w-full bg-surface border border-line focus:border-signal outline-none text-white text-sm px-4 py-3.5 placeholder:text-slate-500 transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-2.5";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  if (authed === null) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-signal" size={28} />
      </main>
    );
  }

  return authed ? <Dashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} /> : <Login onSuccess={() => setAuthed(true)} />;
}

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 pt-16" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-line bg-surface p-10">
        <ShieldCheck size={28} className="text-signal" />
        <h1 className="mt-5 font-display font-extrabold text-3xl text-white tracking-tight">Admin access</h1>
        <p className="mt-2 text-sm text-mist">Restricted area — T-EMRP information requests.</p>
        <form onSubmit={submit} className="mt-8">
          <label htmlFor="admin-email" className={labelCls}>Email</label>
          <input id="admin-email" type="email" data-testid="admin-input-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@temrp.org" />
          <label htmlFor="admin-password" className={`${labelCls} mt-5`}>Password</label>
          <input id="admin-password" type="password" data-testid="admin-input-password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-signal">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" disabled={loading} className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-signal hover:bg-signal-hover disabled:opacity-60 text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Sign in
          </button>
        </form>
      </div>
    </main>
  );
};

const Dashboard = ({ onLogout }) => {
  const [requests, setRequests] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/requests`, { headers: authHeaders() })
      .then((r) => setRequests(r.data))
      .catch(() => { toast.error("Session expired. Please sign in again."); onLogout(); });
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { headers: authHeaders() });
      setRequests((r) => r.filter((x) => x.id !== id));
      toast.success("Request deleted.");
    } catch {
      toast.error("Could not delete request.");
    }
  };

  return (
    <main className="pt-28 pb-24 min-h-screen max-w-7xl mx-auto px-6 lg:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <Eyebrow>Admin</Eyebrow>
          <h1 className="mt-4 font-display font-extrabold text-4xl text-white tracking-tight">Information requests</h1>
          <p className="mt-2 text-sm text-mist font-mono">{requests ? `${requests.length} total` : "Loading..."}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-line hover:border-slate-500 text-slate-300 font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3 transition-colors">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-line hover:border-signal hover:text-signal text-slate-300 font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3 transition-colors">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>

      {!requests ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-signal" size={26} /></div>
      ) : requests.length === 0 ? (
        <div className="border border-line bg-surface p-16 text-center" data-testid="admin-empty">
          <Inbox size={28} className="text-mist mx-auto" />
          <p className="mt-4 text-slate-400">No information requests yet.</p>
        </div>
      ) : (
        <div className="border border-line divide-y divide-line">
          {requests.map((r) => (
            <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-ink hover:bg-surface/60 transition-colors">
              <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="w-full text-left p-5 lg:px-8 grid grid-cols-2 lg:grid-cols-[1.2fr_1.2fr_1fr_auto] gap-4 items-center">
                <div>
                  <p className="font-display font-bold text-white tracking-tight">{r.name}</p>
                  <p className="text-xs text-mist mt-0.5">{r.title}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-300">{r.organization}</p>
                  <p className="text-xs text-mist mt-0.5">{r.org_type}</p>
                </div>
                <div className="hidden lg:block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-signal border border-signal/40 px-2.5 py-1">{r.interest}</span>
                </div>
                <div className="flex items-center gap-4 justify-end">
                  <span className="font-mono text-[10px] text-mist">{new Date(r.created_at).toLocaleDateString()}</span>
                  {expanded === r.id ? <ChevronUp size={15} className="text-mist" /> : <ChevronDown size={15} className="text-mist" />}
                </div>
              </button>
              {expanded === r.id && (
                <div className="px-5 lg:px-8 pb-6 grid sm:grid-cols-2 gap-6 border-t border-line pt-5">
                  <div className="space-y-3 text-sm">
                    <p><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist block mb-1">Email</span><a href={`mailto:${r.email}`} className="text-signal hover:underline">{r.email}</a></p>
                    {r.phone && <p><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist block mb-1">Phone</span><span className="text-slate-300">{r.phone}</span></p>}
                    <p><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist block mb-1">Received</span><span className="text-slate-300">{new Date(r.created_at).toLocaleString()}</span></p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist mb-1">Message</p>
                    <p className="text-sm text-slate-300 leading-relaxed">{r.message || "—"}</p>
                    <button onClick={() => remove(r.id)} data-testid={`admin-delete-${r.id}`} className="mt-5 inline-flex items-center gap-2 border border-line hover:border-signal hover:text-signal text-slate-400 font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 transition-colors">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
};
